import {
  HIDDEN_TIM_GAIN_GROWTH_PER_ALLOCATION_PER_SECOND,
  PASSIVE_RESOURCE_BALANCE_BASE,
} from "@/features/hoverIdle/core/hoverIdleModel";
import {
  appendAppliedRewardId,
  type OfflineRewardSaveMetadata,
} from "@/features/hoverIdle/persistence/offlineRewardSaveMetadata";
import { deserializeHuge, serializeHuge } from "@/shared/numbers";
import { computeTimOfflineReward } from "./computeTimOfflineReward";
import { canonicalDigest } from "./offlineRewardDigest";
import {
  OFFLINE_REWARD_RECEIPT_VERSION,
  type OfflineRewardReceipt,
} from "./offlineRewardReceipt";
import type {
  OfflineRewardInput,
  OfflineRewardResult,
} from "./offlineRewardTypes";

export function createTimOfflineRewardResult(
  input: OfflineRewardInput,
): OfflineRewardResult {
  const timDelta = calculateTimReward(input);
  const serializedTimDelta = serializeHuge(timDelta);
  const rewardId = createOfflineRewardId(input);
  const metadataWithoutReceipt: OfflineRewardSaveMetadata = {
    revision: input.revision + 1,
    baselineAt: input.baseline.committedAt,
    lastReceipt: null,
    appliedRewardIds: appendAppliedRewardId(
      input.startSave.offlineReward.appliedRewardIds,
      rewardId,
    ),
  };
  const finalSaveWithoutReceipt = {
    ...input.startSave,
    economy: {
      ...input.startSave.economy,
      tim: serializeHuge(
        deserializeHuge(input.startSave.economy.tim).add(timDelta),
      ),
    },
    cumulativeStats: {
      ...input.startSave.cumulativeStats,
      totalTimEarned: serializeHuge(
        deserializeHuge(input.startSave.cumulativeStats.totalTimEarned).add(
          timDelta,
        ),
      ),
    },
    timestamps: {
      ...input.startSave.timestamps,
      savedAt: input.baseline.committedAt,
    },
    offlineReward: metadataWithoutReceipt,
  };
  const resultDigest = createOfflineRewardResultDigest({
    finalSaveWithoutReceipt,
    rewardId,
    timDelta: serializedTimDelta,
  });
  const receipt: OfflineRewardReceipt = {
    version: OFFLINE_REWARD_RECEIPT_VERSION,
    rewardId,
    gameElapsedMs: input.gameElapsedMs,
    wasCapped: input.wasCapped,
    timGained: serializedTimDelta,
    resultDigest,
    commitRevision: input.revision + 1,
    baseline: input.baseline,
  };
  return {
    finalSave: {
      ...finalSaveWithoutReceipt,
      offlineReward: {
        ...metadataWithoutReceipt,
        lastReceipt: receipt,
      },
    },
    rewardId,
    timDelta: serializedTimDelta,
    resultDigest,
    receipt,
  };
}

export function calculateTimReward(input: OfflineRewardInput) {
  const balance = input.startSave.economy.hovTimBalance;
  return computeTimOfflineReward({
    gameElapsedMs: input.gameElapsedMs,
    timGainPerSecond: deserializeHuge(input.startSave.economy.timGainPerSecond),
    timAllocation: balance.timAllocation,
    passiveResourceBalanceBase: PASSIVE_RESOURCE_BALANCE_BASE,
    hiddenTimGainGrowthPerSecond:
      balance.timGainAllocation *
      HIDDEN_TIM_GAIN_GROWTH_PER_ALLOCATION_PER_SECOND,
  });
}

export function createOfflineRewardId(input: OfflineRewardInput): string {
  return `tim-offline-reward:${canonicalDigest({
    inputDigest: input.inputDigest,
    baseline: input.baseline,
    gameElapsedMs: input.gameElapsedMs,
    wasCapped: input.wasCapped,
    rewardFormulaVersion: input.rewardFormulaVersion,
  })}`;
}

export function createOfflineRewardResultDigest(value: {
  readonly finalSaveWithoutReceipt: unknown;
  readonly rewardId: string;
  readonly timDelta: string;
}): string {
  return canonicalDigest(value);
}
